// Debounced, cancellable search-as-you-type.
//
// Two properties matter mid-call and both are easy to get wrong by hand:
//
//   * the previous rows stay on screen while the next request runs — blanking the
//     list on every keystroke makes a fast list feel slower than it is;
//   * a superseded request is aborted, so a slow response for "BB-0" can never
//     overwrite the results for "BB-0142".
//
// `fetcher` is called as `(query, signal)` and must be referentially stable —
// wrap it in useCallback, or every render restarts the search.

import { useEffect, useState } from 'react';

import useDebouncedValue from './useDebouncedValue.js';

export default function useTypeahead({ query, fetcher, minLength = 2, delay = 200, enabled = true }) {
  const debounced = useDebouncedValue(query.trim(), delay);
  const [state, setState] = useState({ items: [], status: 'idle', error: null });

  // Below the minimum the API answers 400 INVALID_QUERY_PARAM (docs/API.md §4.1),
  // so the floor is enforced here rather than discovered as an error.
  const isActive = enabled && debounced.length >= minLength;

  useEffect(() => {
    if (!isActive) {
      setState({ items: [], status: 'idle', error: null });
      return undefined;
    }

    const controller = new AbortController();
    let cancelled = false;

    setState((prev) => ({ items: prev.items, status: 'loading', error: null }));

    fetcher(debounced, controller.signal)
      .then((items) => {
        if (!cancelled) setState({ items, status: 'ready', error: null });
      })
      .catch((error) => {
        if (cancelled || error?.name === 'AbortError') return;
        setState({ items: [], status: 'error', error });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [isActive, debounced, fetcher]);

  return { ...state, query: debounced, isActive };
}
